/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.stdlib

import io.peekandpoke.klang.script.KlangScriptLibrary
import io.peekandpoke.klang.script.builder.registerVarargFunction
import io.peekandpoke.klang.script.generated.registerStdlibGenerated
import io.peekandpoke.klang.script.klangScriptLibrary
import io.peekandpoke.klang.script.stdlib.KlangStdLib.defaultOutputHandler

/**
 * KlangScript Standard Library
 *
 * Most registrations are generated from @KlangScript annotations (see stdlib/ files).
 * Console and print remain manual because they need the injected outputHandler.
 *
 * **Usage**:
 * ```kotlin
 * val engine = klangScript {
 *     registerLibrary(KlangStdLib.create())
 * }
 * ```
 */
object KlangStdLib {

    /** Default output handler -- routes to platform-specific console. */
    val defaultOutputHandler: (ConsoleLevel, List<String>) -> Unit = ::platformConsoleOutput

    /**
     * Create the standard library.
     *
     * @param outputHandler Handler for print() and console output.
     *   Receives the [ConsoleLevel] and the list of stringified arguments.
     *   Defaults to [defaultOutputHandler] (platform console). Override to capture output in a REPL or tests.
     * @return A KlangScriptLibrary instance containing all standard functions
     */
    fun create(
        outputHandler: (ConsoleLevel, List<String>) -> Unit = defaultOutputHandler,
    ): KlangScriptLibrary {
        return klangScriptLibrary("stdlib") {
            // No script source and no export list (removed 2026-10-04: it governed nothing). Importing "stdlib"
            // (`import * from "stdlib"`, the line every song starts with) loads the natives below into the engine's
            // native environment, the parent of every script scope, so every stdlib name is then in scope. A selective
            // or namespace import of "stdlib" has no exports to bind (StdLibScopeSpec).

            // Register all annotation-generated symbols:
            // Math, Object, String/Array/Number/Boolean extensions
            registerStdlibGenerated()

            // Console object -- registered manually because output goes through the injected outputHandler.
            // Methods match the JavaScript console API: log, warn, error, info, debug.
            registerObject("console", KlangScriptConsole) {
                registerVarargMethod("log") { args: List<Any?> ->
                    outputHandler(ConsoleLevel.LOG, args.map { it?.toString() ?: "null" })
                }
                registerVarargMethod("warn") { args: List<Any?> ->
                    outputHandler(ConsoleLevel.WARN, args.map { it?.toString() ?: "null" })
                }
                registerVarargMethod("error") { args: List<Any?> ->
                    outputHandler(ConsoleLevel.ERROR, args.map { it?.toString() ?: "null" })
                }
                registerVarargMethod("info") { args: List<Any?> ->
                    outputHandler(ConsoleLevel.INFO, args.map { it?.toString() ?: "null" })
                }
                registerVarargMethod("debug") { args: List<Any?> ->
                    outputHandler(ConsoleLevel.DEBUG, args.map { it?.toString() ?: "null" })
                }
            }

            // print() -- uses the captured outputHandler at LOG level
            registerVarargFunction("print") { args: List<Any?> ->
                outputHandler(ConsoleLevel.LOG, args.map { it?.toString() ?: "null" })
            }
        }
    }
}
